import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated"; // eslint-disable-line
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import compose from 'recompose/compose';
import _ from 'lodash';
import { withStyles } from '@material-ui/core/styles';
import { isAcme } from 'isAcme';

const styles = () => ({
});

class MonthlyBarChart extends PureComponent { // eslint-disable-line react/prefer-stateless-function

  static propTypes = {
    idPrefix: PropTypes.string,
    monthlyData: PropTypes.any,
    onBarGraphClick: PropTypes.func,
    selectedMonthIndex: PropTypes.number,
    active: PropTypes.bool,
    getBarAmount: PropTypes.func,
    tooltipLabel: PropTypes.string,
    colors: PropTypes.object,
    theme: PropTypes.object,
    expandableGrid: PropTypes.bool,
    swlData:PropTypes.object,
    negator: PropTypes.number,
  };
  static defaultProps = {
    getBarAmount: (amount) => amount,
    tooltipLabel: '',
    expandableGrid: false,
    idPrefix: '',
    negator: 1,
  };

  componentDidMount() {
    this.setupChart(this.props.monthlyData, this.props.selectedMonthIndex);
  }

  componentWillReceiveProps(nextProps, _nextContext) {
    const { monthlyData, selectedMonthIndex, swlData, expandableGrid } = this.props;
    if (nextProps.monthlyData !== monthlyData) {
      // for some reason monthlyData shallow compare says monthlyData did change,
      // but in fact the values are all equal, so we have to use deep compare
      if (!_.isEqual(monthlyData, nextProps.monthlyData)) {
        this.chart.invalidateRawData();
        this.setChartData(this.chart, nextProps.monthlyData, nextProps.selectedMonthIndex, nextProps.swlData);
      }
    }
    if (nextProps.selectedMonthIndex !== selectedMonthIndex) {
      this.setChartData(this.chart, nextProps.monthlyData, nextProps.selectedMonthIndex, nextProps.swlData);
    }
    if (expandableGrid && nextProps.swlData !== swlData) {
      this.setChartData(this.chart, nextProps.monthlyData, nextProps.selectedMonthIndex, nextProps.swlData);
    }
    if (nextProps.active !== this.props.active) {
      this.chart.series.each((series) => nextProps.active ? series.show() : series.hide());
    }
    if (nextProps.theme !== this.props.theme) {
      const labelColor = am4core.color(nextProps.theme.applyOpacityToHex(nextProps.theme.palette.greyScale[0], 0.6));
      this.valueAxis.renderer.labels.template.fill = labelColor;
      this.categoryAxis.renderer.labels.template.fill = labelColor;
      this.setTooltipHtml(nextProps.theme, nextProps);
    }
    if (nextProps.tooltipLabel !== this.props.tooltipLabel) {
      this.setTooltipHtml(nextProps.theme, nextProps)
    }
    if(expandableGrid && nextProps.swlData !== this.props.swlData) {
      this.chart.invalidateRawData();
    }
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeThrottler);
    if (this.chart) {
      this.chart.dispose();
    }
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  setupChart = (monthlyData, selectedMonthIndex) => {
    const { idPrefix, theme, expandableGrid } = this.props;
    if (this.resizeThrottler) {
      clearTimeout(this.resizeThrottler);
    }
    window.addEventListener('resize', this.resizeThrottler);
    am4core.options.autoSetClassName = true;
    am4core.useTheme(am4themes_animated);
    const chartDivID = idPrefix
      ? `${idPrefix}-monthly-bar-chart-div`
      : 'monthly-bar-chart-div';
    const chart = am4core.create(chartDivID, am4charts.XYChart);
    this.labelColor = am4core.color(theme.applyOpacityToHex(theme.palette.greyScale[0], 0.6));
    this.labelColorSelected = am4core.color(theme.palette.greyScale[0]);

    chart.numberFormatter.numberFormat = "'$'#,###";

    const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = 'month';
    categoryAxis.tooltip.disabled = true;
    categoryAxis.renderer.grid.template.disabled = true;
    categoryAxis.renderer.grid.template.location = .5;
    categoryAxis.renderer.grid.template.strokeOpacity = 0;
    categoryAxis.renderer.ticks.template.disabled = false;
    categoryAxis.renderer.line.opacity = 1;
    categoryAxis.renderer.minGridDistance = 40;
    categoryAxis.renderer.labels.template.fontSize = 12;
    categoryAxis.renderer.labels.template.fill = this.labelColor;
    categoryAxis.renderer.labels.template.cursorOverStyle = am4core.MouseCursorStyle.pointer;
    categoryAxis.renderer.labels.template.events.on('over', this.onLabelOver, this);
    categoryAxis.renderer.labels.template.events.on('out', this.onLabelOut, this);
    categoryAxis.renderer.labels.template.events.on('hit', this.onBarOrLabelClick, this);

    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.renderer.grid.template.disabled = true;
    valueAxis.tooltip.disabled = true;
    valueAxis.renderer.line.opacity = 0;
    valueAxis.renderer.line.zIndex = -1;
    valueAxis.renderer.ticks.template.disabled = true;
    valueAxis.renderer.labels.template.fontSize = 12;
    valueAxis.renderer.labels.template.fill = this.labelColor;
    valueAxis.renderer.baseGrid.stroke = am4core.color(theme.palette.greyScale[2]);
    valueAxis.renderer.baseGrid.zIndex = 1;
    valueAxis.extraMax = 0.5;

    // Create series
    const series = chart.series.push(new am4charts.ColumnSeries());
    series.showOnInit = true;
    series.dataFields.valueY = 'amount';
    series.dataFields.categoryX = 'month';
    series.sequencedInterpolation = true;

    series.tooltip.label.textAlign = 'middle';
    series.tooltip.dy = -5;
    series.tooltip.getFillFromObject = false;
    series.tooltip.pointerOrientation = 'down';
    series.tooltip.background.fill = am4core.color(theme.palette.background.default);
    series.tooltip.background.fillOpacity = 1;
    series.tooltip.background.strokeWidth = 1;
    series.tooltip.background.cornerRadius = 4;
    series.tooltip.background.pointerLength = 12;
    series.tooltip.background.valign = 'middle';

    const rect = new am4core.Rectangle();
    rect.opacity = 0;
    rect.x=-100;
    rect.y=-100;
    rect.height=100;
    rect.width=200;
    rect.parent=series.tooltip;
    rect.cursorOverStyle = am4core.MouseCursorStyle.pointer;
    rect.events.on('hit', this.onBarOrLabelClick, this);

    series.columns.template.column.cornerRadius(4, 4, 0, 0);
    series.columns.template.width = expandableGrid
      ? am4core.percent(60)
      : am4core.percent(40);
    series.columns.template.column.zIndex = 2;
    series.columns.template.cursorOverStyle = am4core.MouseCursorStyle.pointer;
    series.columns.template.propertyFields.alwaysShowTooltip = 'always';
    series.columns.template.tooltipY = 0;
    series.columns.template.events.on('hit', this.onBarOrLabelClick, this);
    series.columns.template.events.on('over', this.onBarOver, this);
    series.columns.template.events.on('out', this.onBarOut, this);

    this.series = series;
    this.setTooltipHtml(theme);

    // adapters
    series.columns.template.column.adapter.add('cornerRadiusBottomLeft', this.cornerRadiusBottomAdapter);
    series.columns.template.column.adapter.add('cornerRadiusBottomRight', this.cornerRadiusBottomAdapter);
    series.columns.template.column.adapter.add('cornerRadiusTopLeft', this.cornerRadiusTopAdapter);
    series.columns.template.column.adapter.add('cornerRadiusTopRight', this.cornerRadiusTopAdapter);
    series.columns.template.adapter.add('fill', this.fillAdapter);
    series.columns.template.adapter.add('stroke', this.fillAdapter);
    series.columns.template.adapter.add('strokeWidth', this.strokeWidthAdapter);
    series.columns.template.adapter.add('dy', this.dyChange)
    categoryAxis.renderer.labels.template.adapter.add('fill', this.labelFillAdapter);

    // needed for tooltips
    chart.cursor = new am4charts.XYCursor();
    chart.cursor.xAxis = valueAxis;
    chart.cursor.lineX.disabled = true;
    chart.cursor.lineY.disabled = true;
    chart.cursor.behavior = "none";

    chart.maxZoomLevel = 1;
    chart.zoomOutButton.disabled = true;

    this.chart = chart;
    this.categoryAxis = categoryAxis;
    this.valueAxis = valueAxis;
    this.rect = rect;

    this.setChartData(chart, monthlyData, selectedMonthIndex, this.props.swlData);
    if (expandableGrid) {
      const topContainer = chart.chartContainer.createChild(am4core.Container);
      topContainer.layout = "absolute";
      topContainer.toBack();
      topContainer.paddingBottom = 15;
      topContainer.width = am4core.percent(100);
    }
    this.series.tooltip.zIndex = Infinity;
    chart.events.once('datavalidated', () => {
      this.series.tooltip.zIndex = Infinity;
    });
  };

  // https://developer.mozilla.org/en-US/docs/Web/Events/resize
  resizeThrottler = () => {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(this.handleResize, 66);
  };

  handleResize = () => {
    this.chart.svgContainer && this.chart.svgContainer.measure();
  };

  hideTooltips = () => {
    const point = {
      x: -1000,
      y: 0,
      angle: 0,
    }
    this.chart.cursor.triggerMove(point, 'none'); // hides tooltip
  };

  onBarOrLabelClick = (e) => {
    const clickedIndex = e.target.dataItem.index;
    const { selectedMonthIndex, onBarGraphClick } = this.props;
    const isUnselecting = clickedIndex === selectedMonthIndex;
    this.hideTooltips();
    setTimeout(() => {
      onBarGraphClick(isUnselecting ? null : clickedIndex);
    },100);   // for bar clicks, 0 works here, but for label clicks, need the 100.
  };

  onBarOver = (e) => {
    const { index } = e.target.dataItem;
    const dataItem = this.categoryAxis.dataItems.getIndex(index);
    if (dataItem) {
      const { label } = dataItem;
      label.fill = this.labelColorSelected;
      label.fontWeight = 500;
    }
  };

  onBarOut = (e) => {
    const { index } = e.target.dataItem;
    const dataItem = this.categoryAxis.dataItems.getIndex(index);
    if (dataItem) {
      const { label } = dataItem;
      label.fill = this.props.selectedMonthIndex === index ? this.labelColorSelected : this.labelColor;
      label.fontWeight = this.props.selectedMonthIndex === index ? 500 : 'normal';
    }
  };

  // Please keep this code. It prevents zero bars from shifting down (or 'disappearing') when clicked on. If you aren't convinced, slack me.
  dyChange = (dy, target) => {
    if (!target.dataItem) {
      return dy;
    }
    if(this.chart.data[target.dataItem.index].amount > 0) {
      return -2;
    }
    return 2;
  };

  onLabelOver = (e) => {
    // this code triggers onBarOver()
    const point = this.categoryAxis.categoryToPoint(e.target.dataItem.category);
    this.chart.cursor.triggerMove(point, 'soft'); // shows tooltip
  };

  onLabelOut = (e) => {
    // this code triggers onBarOut()
    const point = this.categoryAxis.categoryToPoint(e.target.dataItem.category);
    this.chart.cursor.triggerMove(point, 'none'); // hides tooltip
  };

  // only used on Watchlist page
  setMaxValueAxisWatchlist = (monthlyData, swlData) => {
    const {targetAmount} = swlData;
    const arrayAmount = monthlyData && monthlyData.length && monthlyData.map((a) => this.props.negator * a.amount);
    let monthlyMax = arrayAmount ? Math.max(...arrayAmount) : 0;
    if(monthlyMax === 0 && !targetAmount) {
      monthlyMax = arrayAmount ? Math.min(...arrayAmount) : 0;
      this.valueAxis.max = -1*monthlyMax / 4;
    } else {
      this.valueAxis.max = monthlyMax > targetAmount ? monthlyMax : targetAmount;
    }
  };

  setChartData = (chart, data, selectedMonthIndex, swlData) => {
    const { getBarAmount, expandableGrid } = this.props;
    if(expandableGrid) {
      this.setMaxValueAxisWatchlist(data, swlData);
      if(this.range){
        this.valueAxis.axisRanges.removeValue(this.range);
      }
    }
    const verifiedData = data || [];
    const dataSet = Array.isArray(verifiedData) ? verifiedData : verifiedData.toArray();

    if(expandableGrid && swlData && swlData.targetAmount) {
      // Create value axis range
      const range = this.valueAxis.axisRanges.create();
      range.value = swlData.targetAmount;
      range.grid.fill = am4core.color("#000");
      range.grid.strokeWidth = 1;
      range.grid.strokeOpacity = .7;
      range.label.text = `$${swlData.targetAmount}`;
      range.label.background.fill = am4core.color("#000");
      range.label.fill = am4core.color(this.props.theme.palette.background.paper);
      range.grid.above = true;
      this.range = range;
    }

    chart.data = dataSet.map((period, i) => ({  // eslint-disable-line
      month: period.shortLabel.toUpperCase(),
      amount: expandableGrid ? -1* getBarAmount(period.amount.toFixed(0)) : getBarAmount(period.amount.toFixed(0)),
      monthTitleCase: _.startCase(_.toLower(period.shortLabel)),
      year: String(period.year),
      always: i === selectedMonthIndex,
      target: expandableGrid ? swlData.targetAmount : null,
    }));
  };

  setTooltipHtml = (theme, props = this.props) => {
    const { tooltipLabel, colors, expandableGrid, idPrefix } = props;
    this.series.tooltip.background.fill = am4core.color(theme.palette.background.default);
    if (!expandableGrid) {
      // AmCharts does not support variable substitution within the attributes of the HTML,
      // it only supports variables when they appear as the child of an element.  What we would have
      // liked to do was alter the color of the colored dot based on the associated value, but since
      // we cannot, and since this is only needed for Quicken's NetIncome graph, thus for Quicken only,
      // we omit the colored dot.
      if (isAcme) {
        this.series.columns.template.tooltipHTML = `<div id=${idPrefix}-graph-tooltip xmlns="http://www.w3.org/1999/xhtml" style="
        display: flex;
        justify-content: space-between;
        width: 143px;
        height: 44px;
        font-size: 12px;
        background-color: ${theme.palette.background.default};
        position: static;"
      >
        <div style="display: flex; flex-direction: column; justify-content: center; line-height: 17px; position: static; top: 0; left: 0;">
          <div id=${idPrefix}-graph-tooltip-amount style="font-weight: 500; color: ${theme.applyOpacityToHex(theme.palette.greyScale[0], 0.87)}; letter-spacing: 2px;">{valueY.value}</div>
          <div id=${idPrefix}-graph-tooltip-date" style="font-weight: normal; color: ${theme.applyOpacityToHex(theme.palette.greyScale[0], 0.87)};">{monthTitleCase} {year}</div>
        </div>
        <div style="display: flex; flex-direction: column; justify-content: center; line-height: 17px;">
          <div style="visibility: hidden;">Hidden</div>
          <div style="color: ${theme.applyOpacityToHex(theme.palette.greyScale[0], 0.6)};">
            <div style="display: inline-flex; width: 5px; height: 5px; line-height: 5px; border-radius: 50%; -moz-border-radius: 50%; -webkit-border-radius: 50%; background-color: ${colors.tooltipBullet}; margin-right: 2px; position: static; top: -1px;"></div>
            <div style="display: inline-flex; color: ${theme.applyOpacityToHex(theme.palette.greyScale[0], 0.6)}">${tooltipLabel}</div>
          </div>
        </div>
      </div>`;
        // same as above except omit the colored dot and its associated text
      } else {
        this.series.columns.template.tooltipHTML = `<div id="graph-tooltip" xmlns="http://www.w3.org/1999/xhtml" style="
        display: flex;
        justify-content: space-between;
        width: 70px;
        height: 44px;
        font-size: 12px;
        background-color: ${theme.palette.background.default};
        cursor: pointer !important;
        position: static;"
      >
        <div style="display: flex; flex-direction: column; justify-content: center; line-height: 17px; position: static; top: 0; left: 0;">
          <div id=${idPrefix}-graph-tooltip-amount" style="font-weight: 500; color: ${theme.applyOpacityToHex(theme.palette.greyScale[0], 0.87)}; letter-spacing: 2px;">{valueY.value}</div>
          <div id=${idPrefix}-graph-tooltip-date" style="font-weight: normal; color: ${theme.applyOpacityToHex(theme.palette.greyScale[0], 0.87)};">{monthTitleCase} {year}</div>
        </div>
      </div>`;
      }
    } else {
      // different HTML for the Watchlist graph's tooltips
      this.series.columns.template.tooltipHTML = `<div id="graph-tooltip" xmlns="http://www.w3.org/1999/xhtml" style="
      display: flex;
      justify-content: space-between;
      width: 60px;
      height: 44px;
      font-size: 12px;
      background-color: ${theme.palette.background.default};
      position: static;"
    >
      <div style="display: flex; flex-direction: column; justify-content: center; line-height: 17px; position: static; top: 0; left: 0;">
        <div id=${idPrefix}-graph-tooltip-amount" style="font-weight: 500; color: ${theme.applyOpacityToHex(theme.palette.greyScale[0], 0.87)}; letter-spacing: 2px;">{valueY.value}</div>
        <div id=${idPrefix}-graph-tooltip-date" style="font-weight: normal; color: ${theme.applyOpacityToHex(theme.palette.greyScale[0], 0.87)};">{monthTitleCase} {year}</div>
      </div>
    </div>`
    }
  };

  cornerRadiusBottomAdapter = (radius, target) => {
    if (!target.dataItem) {
      return radius;
    }
    const isNegative = target.dataItem.dataContext.amount < 0;
    return isNegative ? 2 : 0;
  };

  cornerRadiusTopAdapter = (radius, target) => {
    if (!target.dataItem) {
      return radius;
    }
    const isNegative = target.dataItem.dataContext.amount < 0;
    return isNegative ? 0 : 2;
  };

  fillAdapter = (fill, target) => {
    const { colors, selectedMonthIndex} = this.props;
    if (!target.dataItem) {
      return fill;
    }
    const colorDefault = colors.positiveDefault;
    const colorDimmed = colors.positiveDimmed;
    const colorNegative = colors.negativeDefault;
    const colorNegativeDimmed = colors.negativeDimmed;

    const hasSelection = selectedMonthIndex || selectedMonthIndex === 0 && target.dataItem.index;
    const isSelected = hasSelection && selectedMonthIndex === target.dataItem.index;
    const isNegative = target.dataItem.valueY < 0;
    if (hasSelection) {
      if (isNegative) {
        return isSelected ? colorNegative : colorNegativeDimmed;
      }
      return isSelected ? colorDefault : colorDimmed;
    }
    return isNegative ? colorNegative : colorDefault;
  };

  strokeWidthAdapter = (strokeWidth, target) => {
    if (!target.dataItem) {
      return strokeWidth;
    }
    if(this.chart.data[target.dataItem.index].amount === "0" || this.chart.data[target.dataItem.index].amount === 0) {
      return 0;
    }
    return 4;
  };

  labelFillAdapter = (fill, target) => {
    if (!target.dataItem) {
      return fill;
    }
    const { selectedMonthIndex } = this.props;
    const { index } = target.dataItem;
    target.fill = selectedMonthIndex === index  // eslint-disable-line
      ? this.labelColorSelected
      : this.labelColor;
    target.fontWeight = selectedMonthIndex === index ? 500 : 'normal'; // eslint-disable-line
    return fill;
  };

  render() {
    const { idPrefix } = this.props;
    const chartDivID = idPrefix ? `${idPrefix}-monthly-bar-chart-div` : 'monthly-bar-chart-div';
    return (
      <div
        role="presentation"
        onClick={(e) => e.stopPropagation()}  // prevent events from escaping the chart container so that clicks on whitespace and header can be detected and processed correctly
        onKeyPress={(e) => e.stopPropagation()}
        style={{ height: '100%' }}
      >
        <div id={chartDivID} style={{ width: "100%", height: "100%" }} />
      </div>
    );
  }
}

MonthlyBarChart.whyDidYouRender = true;

export default compose(
  withStyles(styles, { withTheme: true }),
)(MonthlyBarChart);
